import { useMemo, useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import {
  FaCloudUploadAlt,
  FaFileExcel,
  FaDownload,
  FaPaperPlane,
  FaEraser,
  FaCheckCircle,
  FaExclamationTriangle,
  FaExclamationCircle,
  FaFileArchive,
  FaTable,
  FaInfoCircle,
} from 'react-icons/fa';
import Rut from 'rutjs';
import * as XLSX from 'xlsx';
import api from '../../api/apiweb-massiveBoletaUploader.js';
import './EmisorMasivo.scss';

const EmisorMasivo = () => {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessageModal, setErrorMessageModal] = useState('');
  const [processedFolios, setProcessedFolios] = useState([]);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  const requiredColumns = [
    'rut',
    'razon social',
    'fecha emision',
    'valor neto',
    'iva',
    'valor total',
    'descripcion',
    'tipodepago',
  ];

  const normalizeKey = (key) =>
    key
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[\s_]+/g, ' ');

  const getColumnValue = (row, aliases) => {
    for (const alias of aliases) {
      if (row[alias] !== undefined && row[alias] !== '') return row[alias];
    }
    return '';
  };

  const processFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const rawData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '' });
      const normalizedData = rawData.map((row) => {
        const normalizedRow = {};
        Object.keys(row).forEach((key) => {
          normalizedRow[normalizeKey(key)] = row[key];
        });
        return normalizedRow;
      });
      validateAndSetData(normalizedData);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileUpload = (event) => processFile(event.target.files[0]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      processFile(file);
    } else {
      setErrors(['Solo se aceptan archivos .xlsx o .xls']);
    }
  };

  const capitalizeHeader = (header) => {
    if (header.toLowerCase() === 'tipodepago') return 'Tipo de Pago';
    return header.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const processRut = (rut) => {
    try {
      const parsed = new Rut(rut);
      if (!parsed.validate()) return { valid: false };
      return {
        valid: true,
        rutWithoutDv: parsed.rut,
        rutFormatted: parsed.getNiceRut(),
      };
    } catch {
      return { valid: false };
    }
  };

  const formatAndValidateDate = (value) => {
    if (!value) return { formatted: '', validFormat: false, isNotFuture: false };
    let formatted = '';
    if (value instanceof Date) {
      formatted = value.toISOString().split('T')[0];
    } else if (typeof value === 'number') {
      const date = XLSX.SSF.parse_date_code(value);
      if (!date) return { formatted: '', validFormat: false, isNotFuture: false };
      formatted = `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
    } else {
      const cleaned = String(value).trim().replace(/[–—]/g, '-').replace(/\s+/g, '');
      if (!/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
        return { formatted: cleaned, validFormat: false, isNotFuture: false };
      }
      formatted = cleaned;
    }
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;
    return { formatted, validFormat: true, isNotFuture: formatted <= todayStr };
  };

  const validateAndSetData = (data) => {
    const headers = Object.keys(data[0] || {});
    const missingCols = requiredColumns.filter((col) => !headers.includes(col));
    if (missingCols.length > 0) {
      setErrors([`Faltan las siguientes columnas: ${missingCols.join(', ')}`]);
      setRows([]);
      return;
    }

    const validatedRows = data.map((row) => {
      const rowErrors = [];

      const { valid, rutWithoutDv, rutFormatted } = processRut(row['rut']);
      if (!valid) {
        rowErrors.push('RUT inválido');
      } else {
        row['rut_display'] = rutFormatted;
        row['rut'] = rutWithoutDv;
      }

      let razon = row['razon social']?.trim();
      if (!razon) {
        rowErrors.push('Razón social vacía');
      } else {
        razon = razon.normalize('NFD').replace(/[̀-ͯ]/g, '');
        razon = razon.replace(/ñ/gi, 'N');
        razon = razon.replace(/[^A-Za-z0-9\s.,-]/g, '');
        row['razon social'] = razon;
        if (razon.length > 60) rowErrors.push('Razón social excede 60 caracteres');
      }

      const valorNeto = parseFloat(row['valor neto']);
      const iva = parseFloat(row['iva']);
      const valorTotal = parseFloat(row['valor total']);
      if (isNaN(valorNeto) || valorNeto < 0) rowErrors.push('Valor neto inválido');
      if (isNaN(iva) || iva < 0) rowErrors.push('IVA inválido');
      if (isNaN(valorTotal) || valorTotal !== valorNeto + iva) rowErrors.push('Valor total no cuadra');

      const rawFecha = getColumnValue(row, ['fecha emision', 'fecha_emision']);
      const { formatted: fechaEmision, validFormat, isNotFuture } = formatAndValidateDate(rawFecha);
      row['fecha emision'] = fechaEmision;
      if (!fechaEmision) {
        rowErrors.push('Fecha de emisión faltante');
      } else {
        if (!validFormat) rowErrors.push('Fecha inválida (yyyy-mm-dd)');
        else if (!isNotFuture) rowErrors.push('Fecha mayor a hoy');
      }

      const rawPago = getColumnValue(row, ['tipodepago', 'tipo de pago', 'tipo_pago']);
      const normal = String(rawPago || '').trim().toLowerCase();
      const mapaPago = {
        'efectivo': 'efectivo',
        'cash': 'efectivo',
        'tarjeta': 'tarjeta',
        'credito': 'tarjeta',
        'crédito': 'tarjeta',
        'debito': 'tarjeta',
        'débito': 'tarjeta',
        'tdc': 'tarjeta',
        'tdd': 'tarjeta',
      };
      const tipodepago = mapaPago[normal];
      if (!tipodepago) {
        rowErrors.push('Tipo de pago inválido');
      } else {
        row['tipodepago'] = tipodepago;
      }

      return { ...row, error: rowErrors.join('; ') };
    });

    setErrors([]);
    setRows(validatedRows);
  };

  const convertKeysToSnakeCase = (row) => ({
    rut: row.rut,
    razon_social: row['razon social'],
    fecha_emision: row['fecha emision'],
    valor_neto: row['valor neto'],
    iva: row.iva,
    valor_total: row['valor total'],
    descripcion: row.descripcion,
    tipodepago: row['tipodepago'],
  });

  const handleSend = () => {
    const hasErrores = rows.some((r) => r.error);
    if (hasErrores) {
      setErrorMessageModal('Algunas filas contienen errores. Revisa los datos y corrige los problemas antes de enviar.');
      setShowErrorModal(true);
      return;
    }
    const cleanRows = rows.map(convertKeysToSnakeCase);
    const jsonString = JSON.stringify(cleanRows);
    setLoading(true);
    api.uploadBoletas(jsonString)
      .then((response) => {
        const payload = response.data?.body ? JSON.parse(response.data.body) : response.data;
        const folios = payload?.folios || payload?.assignedFolios || [];
        setProcessedFolios(folios);
        if (folios.length > 0) {
          setShowSuccessModal(true);
          if (fileInputRef.current) fileInputRef.current.value = '';
          setRows([]);
          setErrors([]);
          setFileName('');
        }
      })
      .catch((error) => {
        setErrorMessageModal('Ocurrió un error al enviar las boletas. Intenta nuevamente.');
        setShowErrorModal(true);
        console.error('Error al enviar las boletas:', error);
      })
      .finally(() => setLoading(false));
  };

  const handleDownloadZip = async () => {
    try {
      setDownloadingZip(true);
      const res = await api.downloadZip({ folios: JSON.stringify(processedFolios) });
      const url = res.data;
      if (!url) {
        console.error('No se recibió una URL válida para el ZIP');
        return;
      }
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error al descargar el ZIP:', err);
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [...requiredColumns];
    const exampleRows = [
      ['12345678-5', 'Juan Pérez', '2025-07-21', 100, 19, 119, 'Estacionamiento mayo 2025', 'efectivo'],
      ['11111111-1', 'María López', '2025-07-21', 200, 38, 238, 'Servicio mensual', 'tarjeta'],
    ];
    const aoa = [headers, ...exampleRows];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [14, 22, 14, 12, 10, 12, 30, 14].map((w) => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, 'plantilla_boletas.xlsx');
  };

  const handleLimpiar = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setRows([]);
    setErrors([]);
    setProcessedFolios([]);
    setFileName('');
  };

  const stats = useMemo(() => {
    if (rows.length === 0) return null;
    const conError = rows.filter((r) => r.error).length;
    return { total: rows.length, validas: rows.length - conError, conError };
  }, [rows]);

  return (
    <div className="EmisorMasivo">
      <div className="page-header">
        <h1 className="page-title">Emisor Masivo de Boletas</h1>
        <p className="page-subtitle">Sube un Excel con boletas y emítelas en lote</p>
      </div>

      <div className="search-card">
        <div className="search-card-header">
          <FaCloudUploadAlt className="me-2" />
          Subir archivo Excel
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleDownloadTemplate}
            className="ms-auto template-btn"
          >
            <FaDownload className="me-2" /><FaFileExcel className="me-1" />Plantilla
          </Button>
        </div>
        <div className="card-body">
          <div
            className={`dropzone ${dragging ? 'dropzone-dragging' : ''} ${fileName ? 'dropzone-with-file' : ''}`}
            onClick={() => fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current.click(); }}
          >
            {fileName ? (
              <>
                <FaFileExcel className="dropzone-icon dropzone-icon-success" />
                <div className="dropzone-title">{fileName}</div>
                <div className="dropzone-hint">Haz clic para reemplazar el archivo</div>
              </>
            ) : (
              <>
                <FaCloudUploadAlt className="dropzone-icon" />
                <div className="dropzone-title">Arrastra tu Excel aquí o haz clic para seleccionar</div>
                <div className="dropzone-hint">Solo archivos .xlsx o .xls</div>
              </>
            )}
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileUpload}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
          </div>

          <div className="info-box">
            <FaInfoCircle className="info-icon" />
            <div>
              <strong>Columnas requeridas:</strong>
              <div className="info-cols">
                {requiredColumns.map((col) => (
                  <code key={col}>{col}</code>
                ))}
              </div>
              <div className="info-sub">El nombre de cada columna debe coincidir exactamente. Descarga la plantilla para ver un ejemplo.</div>
            </div>
          </div>

          {errors.length > 0 && (
            <div className="alert-box alert-danger-box">
              <FaExclamationTriangle className="alert-box-icon" />
              <div>
                <strong>Errores detectados:</strong>
                <ul className="m-0 mt-1">
                  {errors.map((e, idx) => <li key={idx}>{e}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon stat-icon-neutral"><FaTable /></div>
            <div>
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Filas totales</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-success"><FaCheckCircle /></div>
            <div>
              <div className="stat-value">{stats.validas}</div>
              <div className="stat-label">Listas para emitir</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-danger"><FaExclamationCircle /></div>
            <div>
              <div className="stat-value">{stats.conError}</div>
              <div className="stat-label">Con errores</div>
            </div>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="search-card mt-4">
          <div className="search-card-header">
            <FaTable className="me-2" />
            Vista previa
            <span className="results-count">{rows.length}</span>
          </div>
          <div className="p-0">
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    {requiredColumns.map((col) => (
                      <th key={col}>{capitalizeHeader(col)}</th>
                    ))}
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className={row.error ? 'row-error' : 'row-ok'}>
                      {requiredColumns.map((col) => (
                        <td key={col} className={col === 'valor neto' || col === 'iva' || col === 'valor total' ? 'text-end num' : ''}>
                          {col === 'rut' && row['rut_display'] ? row['rut_display'] : row[col]}
                        </td>
                      ))}
                      <td>
                        {row.error ? (
                          <span className="status-pill status-pill-error" title={row.error}>
                            <FaExclamationCircle className="me-1" />
                            {row.error}
                          </span>
                        ) : (
                          <span className="status-pill status-pill-success">
                            <FaCheckCircle className="me-1" />Listo
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="table-footer-actions">
            <Button variant="outline-secondary" onClick={handleLimpiar}>
              <FaEraser className="me-2" />Limpiar
            </Button>
            <Button
              variant="primary"
              className="ms-auto"
              disabled={rows.some((r) => r.error) || loading}
              onClick={handleSend}
            >
              <FaPaperPlane className="me-2" />Emitir boletas
            </Button>
          </div>
        </div>
      )}

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationTriangle className="text-danger me-2" />
            Error al enviar
          </Modal.Title>
        </Modal.Header>
        <Modal.Body><p className="m-0">{errorMessageModal}</p></Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaCheckCircle className="text-success me-2" />
            Boletas emitidas
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Se procesaron correctamente los siguientes folios:</p>
          <div className="folios-box">
            {processedFolios.length > 0 ? (
              <ul className="folios-list">
                {processedFolios.map((f, idx) => <li key={idx}>Folio <strong>{f}</strong></li>)}
              </ul>
            ) : (
              <em>No se recibieron folios.</em>
            )}
          </div>
          <p className="mt-3 mb-0 text-muted">
            Puedes descargar un ZIP con los PDFs y un Excel con el detalle.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSuccessModal(false)}>Cerrar</Button>
          <Button
            variant="primary"
            onClick={handleDownloadZip}
            disabled={downloadingZip || processedFolios.length === 0}
          >
            {downloadingZip ? (
              <><Spinner animation="border" size="sm" className="me-2" />Preparando…</>
            ) : (
              <><FaFileArchive className="me-2" />Descargar ZIP</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={loading} centered backdrop="static">
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="m-0 modal-message">Cargando boletas, por favor espere…</p>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EmisorMasivo;
